<template>
  <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" :value="value" content-class="acq-dialog" max-width="860" persistent scrollable
            transition="dialog-transition" @keydown.esc="close">
    <v-card class="acq-dialog mdlg-scope tp-dialog">
      <div class="mdlg-header">
        <span :class="mode === 'assign' ? 'tone-warn' : 'tone-purple'" class="acq-avatar acq-avatar--sm">
          <v-icon small style="color: inherit">{{ mode === 'assign' ? 'mdi-human-male-board' : 'mdi-bookmark-outline' }}</v-icon>
        </span>
        <div class="mdlg-header__text">
          <div class="acq-section-title">{{ mode === 'assign' ? 'Lehrkräfte zuteilen' : 'Lehrkräfte vormerken' }}</div>
          <div class="acq-tiny text-truncate">{{ schoolName || 'Schule' }}</div>
        </div>
        <v-btn :disabled="saving" aria-label="Schließen" icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <v-card-text class="mdlg-body">
        <teacher-picker v-if="canRead" v-model="selected" :active="value" :initial="people" :institution-id="institutionId"
                        :known-names="preselect && preselectName ? { [preselect]: preselectName } : {}" :mode="mode" @school="schoolName = $event.name"/>
        <div v-else class="acq-tiny py-6 text-center">Keine Berechtigung, Lehrkräfte zu sehen.</div>
      </v-card-text>
      <div class="mdlg-footer">
        <span class="mdlg-footer__state acq-tiny">
          <template v-if="dirty"><span class="mdlg-dot"/>{{ summary }}</template>
          <template v-else>Keine Änderungen</template>
        </span>
        <v-spacer/>
        <button :disabled="saving" class="cta cta--ghost" type="button" @click="close">Abbrechen</button>
        <button :disabled="!dirty || saving" class="cta cta--primary" type="button" @click="save">
          <v-progress-circular v-if="saving" indeterminate size="14" width="2"/>
          <v-icon v-else class="cta__icon" small>mdi-check</v-icon>Speichern
        </button>
      </div>
    </v-card>
    <discard-changes-dialog v-model="confirmDiscard" @confirm="discard"/>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import {Types} from 'mongoose';
import TeacherPicker from './teacherPicker.vue';
import DiscardChangesDialog from '@/components/common/modern/discardChangesDialog.vue';
import {EventBus} from '@/busses/EventBus';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {EmployeeFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/EmployeeFields';
import {asObjectId, PickerMode, saveAssignments} from './teacherPicker.helper';

type Person = { _id: Types.ObjectId; firstName?: string; lastName?: string };

/** Shared dialog of "Lehrkräfte zuteilen" and "Lehrkräfte vormerken": pick several people, then save all changes at once */
export default Vue.extend({
  components: {TeacherPicker, DiscardChangesDialog},
  props: {
    value: {type: Boolean, required: true},
    mode: {type: String as () => PickerMode, required: true},
    institutionId: {type: Object as () => Types.ObjectId, required: true},
    people: {type: Array as () => Person[], required: true},
    /** Optional employee id that is added to the selection when the dialog opens (e.g. "Zuteilen" in the near view) */
    preselect: {type: String, default: ''},
    /** Display name of the preselected person (chip) */
    preselectName: {type: String, default: ''},
  },
  data: () => ({
    selected: [] as string[],
    saving: false,
    confirmDiscard: false,
    schoolName: '',
  }),
  computed: {
    canRead(): boolean {
      return this.$$crudAccessManager.canReadAllProvided(CrudEntityTypes.EMPLOYEE, EmployeeFields.FIRST_NAME, EmployeeFields.LAST_NAME);
    },
    initialIds(): string[] {
      return this.people.map((person) => String(person._id));
    },
    added(): string[] {
      return this.selected.filter((id) => !this.initialIds.includes(id));
    },
    removed(): string[] {
      return this.initialIds.filter((id) => !this.selected.includes(id));
    },
    dirty(): boolean {
      return this.added.length > 0 || this.removed.length > 0;
    },
    summary(): string {
      return [this.added.length ? `${this.added.length} hinzugefügt` : '', this.removed.length ? `${this.removed.length} entfernt` : '']
        .filter(Boolean).join(' · ');
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open: boolean) {
        if (open) {
          this.selected = this.preselect && !this.initialIds.includes(this.preselect) ? [...this.initialIds, this.preselect] : [...this.initialIds];
          this.confirmDiscard = false;
        }
      },
    },
  },
  methods: {
    close() {
      if (this.saving) {
        return;
      }
      if (this.dirty) {
        this.confirmDiscard = true;
        return;
      }
      this.$emit('input', false);
    },
    discard() {
      this.confirmDiscard = false;
      this.$emit('input', false);
    },
    async save() {
      this.saving = true;
      const assign = this.mode === 'assign';
      try {
        const failed = await saveAssignments(this.mode, this.institutionId, {
          added: this.added.map((id) => asObjectId(id)),
          removed: this.removed.map((id) => asObjectId(id)),
        });
        if (failed) {
          this.$$showSnackbar(`${failed} Änderung${failed === 1 ? '' : 'en'} konnte${failed === 1 ? '' : 'n'} nicht gespeichert werden. Bitte erneut versuchen.`,
            'error');
        } else {
          this.$$showSnackbar(assign ? 'Die Zuteilung wurde gespeichert.' : 'Die Vormerkung wurde gespeichert.', 'success');
        }
        this.$emit('update:institution');
        EventBus.$emit('update:institution');
        if (!failed) {
          this.$emit('input', false);
        }
      } finally {
        this.saving = false;
      }
    },
  },
});
</script>

<style>
.tp-dialog .mdlg-body {
  padding-top: 8px !important;
}
</style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/common/modern/modernDialog.css"></style>
